import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { MdRefresh } from 'react-icons/md';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import DeleteRowIcon from '@components/Icons/DeleteRowIcon/DeleteRowIcon';
import { useJobContext } from '@company/state/jobContext';
import { CandidateStage } from '@company/types/job-overview.type';
import { useDismissAllNewApplicants } from '@company/hooks/jobDetailHooks/useDismissNewApplicants';
import { useCandidateCounters } from '@company/hooks/jobDetailHooks/useCandidateCounters';
import { useJobDetailStore } from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import SearchInput from '@company/pages/JobOverview/DetailV2/CandidatesPanel/Filter/SearchInput';
import FilterPopover from '@company/pages/JobOverview/DetailV2/CandidatesPanel/Filter/FilterPopover';
import SortControl from '@company/pages/JobOverview/DetailV2/CandidatesPanel/Filter/SortControl';

export default function CandidatesFilterBar() {
  const activeTab = useJobDetailStore((state) => state.activeTab);
  const { job } = useJobContext();
  const queryClient = useQueryClient();
  const dismissAll = useDismissAllNewApplicants();
  const { data: counters } = useCandidateCounters(job?.id ?? 0);
  const isRefreshing =
    useIsFetching({ queryKey: [QueryKeys.JobDetail.CANDIDATES] }) > 0;

  // Dismiss only applies to the New and Applicants tabs, and only when there's
  // something to dismiss.
  const showDismiss =
    activeTab === CandidateStage.NEW || activeTab === CandidateStage.APPLICANTS;
  const canDismiss = showDismiss && !!job && !!counters?.newApplicants;

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.JobDetail.CANDIDATES],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.JobDetail.CANDIDATE_COUNTERS],
    });
  };

  const handleDismissAll = () => {
    if (job) dismissAll.mutate(job.id);
  };

  return (
    <Group my={10}>
      <SearchInput key={activeTab} />

      <FilterPopover />
      <SortControl />

      <Tooltip label="Refresh" withArrow>
        <ActionIcon
          variant="light"
          size={40}
          c="var(--black)"
          aria-label="Refresh"
          onClick={handleRefresh}
          loading={isRefreshing}
        >
          <MdRefresh size={20} />
        </ActionIcon>
      </Tooltip>
      {showDismiss && (
        <Tooltip label="Dismiss" withArrow>
          <ActionIcon
            variant="light"
            size={40}
            c="var(--black)"
            aria-label="Dismiss"
            onClick={handleDismissAll}
            loading={dismissAll.isPending}
            disabled={!canDismiss}
          >
            <DeleteRowIcon size={20} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
